/**
 * Ko'rsatkich raqamini ko'rinish maydoniga kirganda 0 dan yakuniy qiymatgacha
 * sanaydi. Kirish qiymati kontentdagi tayyor satr ("48 260", "94,2%",
 * "12 daq") bo'lgani uchun raqamli qism ajratib olinadi, prefiks va suffiks
 * o'zgarishsiz qoladi — tarjima va o'lchov birligi buzilmaydi.
 *
 * `prefers-reduced-motion` yoqilgan bo'lsa yoki IntersectionObserver mavjud
 * bo'lmasa, qiymat darhol to'liq ko'rinadi.
 */
import { useEffect, useRef, useState } from "react";

import { isInViewport, prefersReducedMotion, watchViewport } from "@/shared/lib/motion";

const DURATION = 1100;
/** Uzuvchi bo'lmagan probel — raqam guruhi satr oxirida ajralib ketmasligi uchun. */
const NBSP = " ";
/** Kontentda uchraydigan "yupqa" probellar oddiy probelga keltiriladi. */
const EXOTIC_SPACE = /[   ]/g;
const NUMBER_RE = /^(\D*?)([\d ]*\d(?:,\d+)?)([\s\S]*)$/;

interface Parsed {
  prefix: string;
  suffix: string;
  target: number;
  decimals: number;
  grouped: boolean;
}

function parse(raw: string): Parsed | null {
  const match = raw.replace(EXOTIC_SPACE, " ").match(NUMBER_RE);
  if (!match) return null;

  const digits = match[2];
  const decimalPart = digits.split(",")[1];
  const target = Number(digits.replace(/ /g, "").replace(",", "."));
  if (!Number.isFinite(target)) return null;

  return {
    prefix: match[1],
    suffix: match[3],
    target,
    decimals: decimalPart ? decimalPart.length : 0,
    grouped: digits.indexOf(" ") >= 0,
  };
}

function render(parsed: Parsed, current: number): string {
  let body = current.toFixed(parsed.decimals).replace(".", ",");
  if (parsed.grouped) {
    const parts = body.split(",");
    const spaced = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
    body = parts[1] ? spaced + "," + parts[1] : spaced;
  }
  return parsed.prefix + body + parsed.suffix;
}

export function CountUp({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const parsed = parse(value);
  const [text, setText] = useState(() => (parsed ? render(parsed, 0) : value));

  useEffect(() => {
    const node = ref.current;
    const target = parse(value);
    if (!target || !node) return;

    /* Harakat so'ralmagan bo'lsa yoki sahifa ko'rinmayotgan bo'lsa (fon
       ilovasi, chop etish, avtomatik tekshiruv) — `requestAnimationFrame`
       ishlamaydi va raqam «0» bo'lib qotib qolardi: darhol yakuniy qiymat. */
    if (prefersReducedMotion() || document.visibilityState === "hidden") {
      setText(value);
      return;
    }

    let frame = 0;
    let start = 0;
    let unwatch = () => {};

    const step = (now: number) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / DURATION);
      // easeOutCubic — oxirida yumshoq to'xtaydi
      const eased = 1 - Math.pow(1 - t, 3);
      if (t < 1) {
        setText(render(target, target.target * eased));
        frame = window.requestAnimationFrame(step);
      } else {
        setText(value);
      }
    };

    unwatch = watchViewport(() => {
      // chegara 1.0 — ko'rinadigan har qanday raqam darhol sanay boshlaydi,
      // aks holda ekran chetidagi ko'rsatkich "0" bo'lib turib qolardi
      if (start || frame || !isInViewport(node, 1)) return;
      unwatch();
      frame = window.requestAnimationFrame(step);
    });

    return () => {
      unwatch();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [value]);

  return (
    <span ref={ref} className={className}>
      {parsed ? text : value}
    </span>
  );
}
